import { z } from "zod";
import { BillboardId, Cents, Coins, IsoUtc, PropertyId, Uuid } from "./primitives";

/**
 * Privacy: data export and account deletion (M7, ADR-0037; docs/08 section 8).
 *
 * Founder decisions (2026-10-22):
 * - "Return holdings": a deleted account's stores and home go back to the town for sale; its coins and Townsy Credit
 *   (non-withdrawable anyway) are forfeited. The resident sees a clear list of what they lose and must confirm.
 *   Payment and ledger records are kept with their name removed.
 * - "7 days": the resident can sign in and cancel within 7 days; then the account is anonymised. Open payments,
 *   refunds or disputes always finish first.
 *
 * Founder decisions (2026-10-23):
 * - Unspent card-paid campaign budget: "Forfeit" (like coins and credit; no refund). Sponsored tasks other residents
 *   are doing on the account's campaigns finish or expire first.
 * - A store returned to the town is for sale at its "Original price" (the administrator-set initial price).
 * - Uploaded logos and images: "Delete after 30 days" (removed from storage 30 days after the deletion completed,
 *   unless a safety case holds them).
 */

/** Days after a completed deletion before the account's uploads are deleted from storage (founder 2026-10-23). */
export const DELETED_ACCOUNT_UPLOAD_RETENTION_DAYS = 30;

/** Days between the request and the earliest processing (founder 2026-10-22: "7 days"). */
export const DELETION_WINDOW_DAYS = 7;
/** What the resident types to confirm (the server compares it exactly, after trimming). */
export const DELETION_CONFIRMATION_WORD = "DELETE";
/** The neutral label a deleted account shows wherever its name used to appear. Never a valid display name (space). */
export const FORMER_RESIDENT_LABEL = "Former resident";
/** The downloaded file's format marker and version. */
export const PRIVACY_EXPORT_FORMAT = "townsy-data-export";
export const PRIVACY_EXPORT_VERSION = 1;

/** Why a due deletion waits. Each code has one plain message (deletionDelayMessage); counts make it exact. */
export const DeletionDelayCode = z.enum([
  /** Orders still being paid for, fulfilled, refunded or reviewed. */
  "open_order",
  /** A refund to the card has not arrived yet. */
  "refund_pending",
  /** A payment dispute or support case is open. */
  "financial_case",
  /** A safety review (report) involving the account is open. */
  "case_hold",
  /** Another resident is checking out one of the account's stores. */
  "store_checkout",
  /** A sponsored task the resident started is still running. */
  "task_in_progress",
  /**
   * Someone else is doing a sponsored task of one of the account's paid campaigns (its reward slot holds budget). The
   * budget is forfeited once every such attempt finished or expired (founder 2026-10-23: "Forfeit").
   */
  "campaign_task_in_progress",
  /**
   * LEGACY, no longer produced (founder 2026-10-23: unspent budget is forfeited instead of delaying deletion). Kept so
   * a reason stored by an earlier release stays readable until that request's next attempt replaces it.
   */
  "campaign_budget",
  /** One of the account's properties is suspended by staff. */
  "property_suspended",
  /** Coins or Townsy Credit are held by a purchase that has not finished. */
  "held_balance",
  /** The account has a Townsy staff role. */
  "staff_role",
]);
export type DeletionDelayCode = z.infer<typeof DeletionDelayCode>;

export const DeletionDelay = z.object({
  code: DeletionDelayCode,
  /** How many (orders, cases...), when counting makes the message exact; null otherwise. */
  count: z.number().int().min(0).nullable(),
  message: z.string(),
});
export type DeletionDelay = z.infer<typeof DeletionDelay>;

const plural = (n: number | null, one: string, many: string) => ((n ?? 1) === 1 ? one : many.replace("{n}", String(n)));

/** The plain-language reason shown on the account page (and stored only as a code + count). */
export function deletionDelayMessage(code: DeletionDelayCode, count: number | null = null): string {
  switch (code) {
    case "open_order":
      return `${plural(count, "One of your orders is", "{n} of your orders are")} still being paid for, completed or refunded. We delete your account once ${plural(count, "it has", "they have")} finished.`;
    case "refund_pending":
      return `${plural(count, "A refund to your card is", "{n} refunds to your card are")} still on the way. We delete your account once ${plural(count, "it has", "they have")} arrived.`;
    case "financial_case":
      return "A payment dispute or support case on your account is still open. We delete your account once it is closed.";
    case "case_hold":
      return "A review by Townsy staff involving your account is still open. We delete your account once it is closed.";
    case "store_checkout":
      return "Someone is buying one of your stores right now. We wait until that checkout has ended.";
    case "task_in_progress":
      return "A sponsored task you started is still running. It ends by itself within its time limit.";
    case "campaign_task_in_progress":
      return "Someone is doing one of your sponsored tasks right now. It ends by itself within its time limit; then your unused task budget is forfeited.";
    case "campaign_budget":
      return "Your unused sponsored-task budget was being settled. We check again shortly.";
    case "property_suspended":
      return "One of your stores or homes is suspended by Townsy staff. We wait until staff have reviewed it.";
    case "held_balance":
      return "Some of your coins or Townsy Credit are held by a purchase that has not finished.";
    case "staff_role":
      return "Your account has a Townsy staff role. Another administrator must remove it before the account can be deleted.";
  }
}

export const DeletionRequestState = z.enum(["pending", "canceled", "completed"]);
export type DeletionRequestState = z.infer<typeof DeletionRequestState>;

export const DeletionRequestView = z.object({
  requestId: Uuid,
  state: DeletionRequestState,
  requestedAt: IsoUtc,
  /** The earliest time the account is deleted (requestedAt + 7 days). */
  scheduledFor: IsoUtc,
  canceledAt: IsoUtc.nullable(),
  completedAt: IsoUtc.nullable(),
  /** Why a due request still waits, as the last processing attempt found it ([] when nothing delays it). */
  waiting: z.array(DeletionDelay),
  lastAttemptAt: IsoUtc.nullable(),
  /** When the worker looks again (pending requests only). */
  nextAttemptAt: IsoUtc.nullable(),
  /** True while pending: the resident can still cancel. */
  canCancel: z.boolean(),
});
export type DeletionRequestView = z.infer<typeof DeletionRequestView>;

export const DeletionImpactStore = z.object({ propertyId: PropertyId, address: z.string() });
export const DeletionImpactHome = z.object({
  propertyId: PropertyId,
  address: z.string(),
  /** Home stage id (tent ... castle). */
  stage: z.string(),
  /** Items placed in the yard or inside. */
  placedItems: z.number().int().min(0),
});
export const DeletionImpactRental = z.object({
  rentalId: Uuid,
  billboardId: BillboardId,
  label: z.string(),
  startDate: z.string(),
  /** Last UTC day the rental would have shown (inclusive). */
  lastDay: z.string(),
});
export const DeletionImpactOffers = z.object({
  campaignId: Uuid,
  title: z.string(),
  propertyId: PropertyId.nullable(),
  address: z.string().nullable(),
  /** Offers visitors can do now (or once approved). */
  offers: z.number().int().min(0),
});
/** A paid (card-funded) campaign budget the account would forfeit (founder 2026-10-23: "Forfeit"; never refunded). */
export const DeletionImpactBudget = z.object({
  campaignId: Uuid,
  title: z.string(),
  /** Unspent and free now. */
  availableCents: Cents,
  /** Held by sponsored tasks other residents are doing now: spent if they finish, back to available if they expire. */
  reservedCents: Cents,
});

/** GET /me/privacy/deletion-impact: exactly what deleting the account would take away, and what delays it. */
export const DeletionImpact = z.object({
  windowDays: z.number().int().min(1),
  confirmationWord: z.string(),
  /** True when the account has a password: the request must repeat it. Otherwise a recent sign-in is required. */
  passwordRequired: z.boolean(),
  stores: z.array(DeletionImpactStore),
  homes: z.array(DeletionImpactHome),
  /** Home items the account owns (placed or in storage). */
  inventoryItems: z.number().int().min(0),
  coins: Coins,
  creditCents: Cents,
  billboardRentals: z.array(DeletionImpactRental),
  runningOffers: z.array(DeletionImpactOffers),
  /** Unspent card-paid sponsored-task budgets, forfeited at deletion (no refund). */
  campaignBudgets: z.array(DeletionImpactBudget).default([]),
  /** What would delay processing if the account were due now. */
  delays: z.array(DeletionDelay),
  request: DeletionRequestView.nullable(),
});
export type DeletionImpact = z.infer<typeof DeletionImpact>;

/** POST /me/privacy/deletion (with an Idempotency-Key header). */
export const DeletionRequestBody = z
  .object({
    /** The account password (required when the account has one; never stored or logged). */
    password: z.string().min(1).max(512).optional(),
    /** Must be DELETION_CONFIRMATION_WORD. */
    confirmation: z.string().max(40),
    /** The resident saw the list of what they lose. */
    acknowledgeLoss: z.literal(true),
  })
  .strict();
export type DeletionRequestBody = z.infer<typeof DeletionRequestBody>;

export const DeletionStatusResponse = z.object({ request: DeletionRequestView.nullable() });
export type DeletionStatusResponse = z.infer<typeof DeletionStatusResponse>;

// ---------------------------------------------------------------------------------------------
// Staff: GET /api/v1/admin/privacy-requests (privacy.requests.manage; read-only)
// ---------------------------------------------------------------------------------------------

export const PrivacyRequestType = z.enum(["export", "deletion"]);
export type PrivacyRequestType = z.infer<typeof PrivacyRequestType>;

/** Filters (both optional), cursor pagination, newest first. Unknown parameters are ignored. */
export const AdminPrivacyRequestsQuery = z.object({
  type: PrivacyRequestType.optional(),
  state: DeletionRequestState.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
});
export type AdminPrivacyRequestsQuery = z.infer<typeof AdminPrivacyRequestsQuery>;
export const ADMIN_PRIVACY_REQUESTS_DEFAULT_LIMIT = 50;

/** One request as staff see it: the account id only (never an email or a name), the dates, and why it waits. */
export const AdminPrivacyRequestItem = z.object({
  requestId: Uuid,
  userId: z.string(),
  type: PrivacyRequestType,
  state: DeletionRequestState,
  requestedAt: IsoUtc,
  /** Deletions: the earliest processing time (requested + 7 days). Null for exports. */
  scheduledFor: IsoUtc.nullable(),
  completedAt: IsoUtc.nullable(),
  canceledAt: IsoUtc.nullable(),
  /** Pending deletions: why the last attempt waited ([] otherwise). */
  waiting: z.array(DeletionDelay),
  attempts: z.number().int().min(0),
  lastAttemptAt: IsoUtc.nullable(),
  nextAttemptAt: IsoUtc.nullable(),
  /** Completed deletions: when the account's uploads were deleted from storage (null until then, or while held). */
  uploadsPurgedAt: IsoUtc.nullable(),
});
export type AdminPrivacyRequestItem = z.infer<typeof AdminPrivacyRequestItem>;
export const AdminPrivacyRequestsResponse = z.object({ items: z.array(AdminPrivacyRequestItem), nextCursor: z.string().nullable() });
export type AdminPrivacyRequestsResponse = z.infer<typeof AdminPrivacyRequestsResponse>;
